/**
 * Spellcasting Entries (issue #10, docs/adr/0003): one per Casting Source the Character holds.
 *
 * Foundry keeps Spellcasting Entries as items the user creates by hand; nothing in its data
 * says that a wizard casts arcane prepared spells from Intelligence with the Wizard table's
 * slots. This module derives them: the class Entry (with the Classes journal's slot table and
 * CLASS_CASTING), multiclass spellcasting dedications with their basic/expert/master feats, the
 * cleric's divine font, a wizard school's curriculum slots, innate spells the held Entries'
 * descriptions grant, focus spells they grant or offer, and Entries the user declared by hand;
 * since #21 also archetype spellcasting a feat describes in prose (Red Mantis Magic), a
 * spellbook a feat gives a spontaneous caster (Arcane Evolution), the attuned apparitions'
 * repertoire and vessel spells, and the staves in the inventory with their charges (ADR-0013).
 * The user's own spells (repertoire, spellbook, chosen innate and focus spells, preparations)
 * come from `character.spellcasting`; spells a Casting Source grants are never stored.
 *
 * Three phases mirror Foundry's: `collectCastingSources` runs after structural preparation and
 * before Rule Elements (it sets the spellcasting proficiency minimums, the focus pool and the
 * `self:caster` roll options); `spellcastingStatistics` builds each Entry's attack and DC with
 * the base spellcasting statistic's domains plus the tradition's; `spellcastingViews` resolves
 * the spells at their cast ranks for the sheet.
 */
import type { Entry, IndexRecord } from "../../shared/snapshot-format";
import type { CastingType, Character, MagicTradition, SpellcastingState } from "../character/model";
import { MAGIC_TRADITIONS } from "../character/model";
import type { EngineItem } from "./items";
import { EnginePhysical } from "./physical";
import { sluggify } from "./slug";
import { ARCHETYPE_SLOT_LEVELS, CASTING_LABELS, CLASS_CASTING, FOCUS_OWNERS, HARM_UUID, HEAL_UUID, SPELLCASTING_DEDICATION_TAG, TRADITION_LABELS, classSlots, divineFontSlots, highestRank } from "./spellcasting-tables";
import { featureReferences, formStatements, grantSentences, innateStatements, itemSpellList, labelledSpellLists, spellListTradition, spellbookStatement, spellcastingAttribute, spellcastingBenefits, type SpellLink, type SpellcastingBenefits } from "./spell-text";
import { castRank, heightenSpell, isCantrip, isFocusSpell, isRitual, spellBaseRank, spellTraditions, type HeightenedDamage } from "./spells";
import { calculateSpellDamage } from "./spell-damage";
import type { SpellDamageView } from "./engine";
import type { EngineState } from "./state";
import { buildStatistic, type DerivedStatistic } from "./statistics";
import { damageDiceView, modifierView } from "./views";
import { isAttribute, type Attribute, type Rank } from "./values";

export type SourceKind = "class" | "archetype" | "font" | "curriculum" | "spellbook" | "innate" | "focus" | "item" | "manual";
type KnownSpell = import("../character/model").KnownSpell;

/** A spell a Casting Source gives on its own. */
export interface GrantedSpell {
  uuid: string;
  name: string;
  /** The rank it is known or cast at, when the source says; null for the spell's own (or auto-heightened). */
  rank: number | null;
  /** Innate: uses per day; null for at will. */
  uses: number | null;
  granter: EngineItem;
}

export interface SpellChoiceFilter {
  cantrip: boolean;
  /** The highest base rank a candidate may have (null: any). */
  rank: number | null;
  tradition: MagicTradition | null;
  /** Focus spells only. */
  focus: boolean;
  /** A trait every candidate must carry (a class trait). */
  trait: string | null;
}

/** A spell pick a Casting Source asks the user for. */
export interface SpellChoice {
  key: string;
  granter: EngineItem;
  prompt: string;
  /** Explicit candidates; null when any spell passing `filter` may be picked. */
  candidates: string[] | null;
  filter: SpellChoiceFilter;
  /** Innate Choices: uses per day of the chosen spell; null for at will. */
  uses: number | null;
}

export interface CastingSource {
  id: string;
  kind: SourceKind;
  name: string;
  /** The Entry that opens this Spellcasting Entry; null for a hand-declared one. */
  item: EngineItem | null;
  /** The class the source casts as (a class or its dedication), if any. */
  classSlug: string | null;
  tradition: MagicTradition | null;
  casting: CastingType;
  attribute: Attribute;
  /** "base" is the Character's spellcasting proficiency; innate Entries have their own floor. */
  rank: Rank | "base";
  /** Slots per rank at this Level: index 0 cantrips, 1 to 10 ranks. */
  slots: number[];
  preparesFrom: "spellbook" | "tradition" | null;
  /** Only these spells may be prepared here (the divine font's, a curriculum's). */
  restrictedTo: string[] | null;
  /** The source whose statistic this one uses (the font and curriculum share the class's). */
  statisticOf: string | null;
  /** Every slot holds this spell (the divine font). */
  autoPrepared: string | null;
  /** A staff's charges (#21): the highest spell rank the Character casts, set at daily preparations; null elsewhere. */
  charges: number | null;
  granted: GrantedSpell[];
  choices: SpellChoice[];
  problems: string[];
}

export interface SpellcastingSourcesResult {
  sources: CastingSource[];
  /** Casting Sources the app's table does not know: a spellcasting feat with no Entry. */
  unrecognised: { uuid: string; name: string; reason: string }[];
}

/* ------------------------------------------------------------------------ */
/*  Catalogue helpers                                                        */
/* ------------------------------------------------------------------------ */

interface SpellFacts {
  uuid: string;
  name: string;
  baseRank: number;
  cantrip: boolean;
  focus: boolean;
  ritual: boolean;
  traditions: MagicTradition[];
  traits: string[];
  rarity: string | null;
  remaster: boolean | null;
  entry: Entry | null;
}

function factsFromRecord(record: IndexRecord): SpellFacts {
  const traits = record.tr;
  const traditions = (record.td ?? []).filter((t): t is MagicTradition => (MAGIC_TRADITIONS as readonly string[]).includes(t));
  return {
    uuid: record.u,
    name: record.n,
    baseRank: Math.min(10, Math.max(1, record.l ?? 1)),
    cantrip: traits.includes("cantrip"),
    focus: traits.includes("focus") || (traits.includes("cantrip") && traditions.length === 0),
    ritual: false,
    traditions,
    traits,
    rarity: record.r,
    remaster: record.rm,
    entry: null,
  };
}

function factsFromEntry(entry: Entry): SpellFacts {
  return {
    uuid: entry.uuid,
    name: entry.name,
    baseRank: spellBaseRank(entry),
    cantrip: isCantrip(entry),
    focus: isFocusSpell(entry),
    ritual: isRitual(entry),
    traditions: spellTraditions(entry),
    traits: entry.traits,
    rarity: entry.rarity,
    remaster: entry.publication.remaster,
    entry,
  };
}

/** What the engine knows about a spell: the loaded Entry, else the index record, else the UUID alone. */
export function spellFacts(state: EngineState, uuid: string): SpellFacts | null {
  const entry = state.entries.get(uuid);
  if (entry && entry.type === "spell") return factsFromEntry(entry);
  const record = state.catalogue?.byUuid(uuid);
  if (record && record.t === "spell") return factsFromRecord(record);
  return null;
}

function nameOf(state: EngineState, uuid: string): string {
  return spellFacts(state, uuid)?.name ?? uuid.slice(uuid.lastIndexOf(".") + 1);
}

function description(item: EngineItem): string {
  return item.description;
}

function title(slug: string): string {
  return slug.split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
}

/* ------------------------------------------------------------------------ */
/*  Phase A: Casting Sources                                                 */
/* ------------------------------------------------------------------------ */

/** The tag Foundry puts on the animist's apparition class features. */
export const APPARITION_TAG = "animist-apparition";

const TRADITION_TAG_BY_CLASS: Readonly<Record<string, string>> = { sorcerer: "sorcerer-bloodline", witch: "witch-patron", summoner: "summoner-eidolon" };
const TRADITION_OPTION_BY_CLASS: Readonly<Record<string, string>> = { sorcerer: "bloodline" };

/** The tradition a class whose casting a feature names resolves to: a `feature:<x>:tradition:<t>` roll option, or a Spell List / Tradition label on the tagged feature. */
function featureTradition(state: EngineState, classSlug: string): MagicTradition | null {
  const option = TRADITION_OPTION_BY_CLASS[classSlug];
  if (option) {
    for (const o of state.rollOptions.get(["all"])) {
      const m = new RegExp(`^feature:${option}:tradition:(arcane|divine|occult|primal)$`).exec(o);
      if (m) return m[1] as MagicTradition;
    }
  }
  const tag = TRADITION_TAG_BY_CLASS[classSlug];
  if (tag) {
    for (const item of state.items) {
      if (!item.otherTags.includes(tag)) continue;
      const tradition = spellListTradition(description(item));
      if (tradition) return tradition;
    }
  }
  return null;
}

function keyAttribute(state: EngineState): Attribute {
  return state.actor.system.details.keyability.value;
}

/** A held item's class traits among the classes the tables know. */
function classTraitsOf(item: EngineItem): string[] {
  return item.traits.filter((t) => t in CLASS_CASTING || t in FOCUS_OWNERS);
}

interface Collector {
  state: EngineState;
  character: Character;
  level: number;
  sources: CastingSource[];
  byId: Map<string, CastingSource>;
  unrecognised: SpellcastingSourcesResult["unrecognised"];
}

function source(c: Collector, id: string, init: () => CastingSource): CastingSource {
  let s = c.byId.get(id);
  if (!s) {
    s = init();
    c.byId.set(id, s);
    c.sources.push(s);
  }
  return s;
}

/** Spells a labelled list or grant sentence names, as facts, dropping links to nothing. */
function factsOf(state: EngineState, links: SpellLink[]): SpellFacts[] {
  return links.map((l) => spellFacts(state, l.uuid) ?? null).filter((f): f is SpellFacts => f !== null);
}

/** The class source of the class (or its dedication) the item belongs to, for granted spells. */
function classSourceFor(c: Collector, item: EngineItem): CastingSource | null {
  const traits = new Set(item.traits);
  return c.sources.find((s) => (s.kind === "class" || s.kind === "archetype") && s.classSlug && traits.has(s.classSlug)) ?? null;
}

function roleRank(role: string | number | null): number | null {
  if (role === "cantrip") return 0;
  return typeof role === "number" ? role : null;
}

/** Add a granted known spell to a slotted source when its rank is castable (cantrips always). */
function grantKnown(c: Collector, target: CastingSource, facts: SpellFacts, rank: number | null, granter: EngineItem): void {
  if (facts.focus || facts.ritual) return;
  const at = facts.cantrip ? 0 : (rank ?? facts.baseRank);
  if (at > 0 && at > highestRank(target.slots)) return;
  if (target.granted.some((g) => g.uuid === facts.uuid && (g.rank ?? null) === (facts.cantrip ? null : at))) return;
  target.granted.push({ uuid: facts.uuid, name: facts.name, rank: facts.cantrip ? null : at, uses: null, granter });
}

/** The class slug the item's focus spells belong to: its own class trait, else the spell's, preferring the Character's class. */
function focusOwner(c: Collector, granter: EngineItem, facts: SpellFacts | null): string | null {
  const own = c.state.actor.system.details.class?.trait ?? null;
  const candidates = [...classTraitsOf(granter), ...(facts ? facts.traits.filter((t) => t in CLASS_CASTING || t in FOCUS_OWNERS) : [])];
  if (own && candidates.includes(own)) return own;
  if (candidates.length > 0) return candidates[0]!;
  return own;
}

function focusSource(c: Collector, granter: EngineItem, facts: SpellFacts | null): CastingSource {
  const owner = focusOwner(c, granter, facts) ?? "focus";
  return source(c, `focus:${owner}`, () => {
    const classSource = c.sources.find((s) => (s.kind === "class" || s.kind === "archetype") && s.classSlug === owner);
    const fixed = FOCUS_OWNERS[owner];
    const tradition = classSource?.tradition ?? fixed?.tradition ?? (CLASS_CASTING[owner]?.tradition !== "feature" ? (CLASS_CASTING[owner]?.tradition as MagicTradition | undefined) ?? null : null);
    const attribute = spellcastingAttribute(description(granter)) ?? classSource?.attribute ?? fixed?.attribute ?? (CLASS_CASTING[owner] && CLASS_CASTING[owner].attribute !== "key" ? (CLASS_CASTING[owner].attribute as Attribute) : null) ?? "cha";
    const problems: string[] = [];
    if (!tradition) problems.push(`The tradition of ${title(owner)} focus spells is unknown; a manual Spellcasting Entry can replace this one.`);
    return {
      id: `focus:${owner}`,
      kind: "focus",
      name: `${title(owner)} Focus Spells`,
      item: granter,
      classSlug: owner,
      tradition,
      casting: "focus",
      attribute,
      rank: "base",
      slots: Array(11).fill(0),
      preparesFrom: null,
      restrictedTo: null,
      statisticOf: null,
      autoPrepared: null,
      charges: null,
      granted: [],
      choices: [],
      problems,
    };
  });
}

function innateSource(c: Collector, tradition: MagicTradition, granter: EngineItem): CastingSource {
  const s = source(c, `innate:${tradition}`, () => ({
    id: `innate:${tradition}`,
    kind: "innate",
    name: `${TRADITION_LABELS[tradition]} Innate Spells`,
    item: granter,
    classSlug: null,
    tradition,
    casting: "innate",
    attribute: "cha",
    rank: "base",
    slots: Array(11).fill(0),
    preparesFrom: null,
    restrictedTo: null,
    statisticOf: null,
    autoPrepared: null,
    charges: null,
    granted: [],
    choices: [],
    problems: [],
  }));
  const attribute = spellcastingAttribute(description(granter));
  if (attribute && s.attribute === "cha" && attribute !== "cha") s.attribute = attribute;
  return s;
}

function grantFocus(c: Collector, granter: EngineItem, facts: SpellFacts): void {
  const target = focusSource(c, granter, facts);
  if (target.granted.some((g) => g.uuid === facts.uuid)) return;
  target.granted.push({ uuid: facts.uuid, name: facts.name, rank: null, uses: null, granter });
}

let choiceCounter = 0;

function addChoice(target: CastingSource, granter: EngineItem, prompt: string, candidates: string[] | null, filter: SpellChoiceFilter, uses: number | null = null): void {
  const base = `${granter.choiceKey ?? granter.uuid}`;
  const n = target.choices.filter((ch) => ch.key.startsWith(`${base}#`)).length;
  target.choices.push({ key: `${base}#${n}`, granter, prompt, candidates, filter, uses });
  choiceCounter++;
}

/** The class and archetype sources: slots from the tables, tradition and attribute from the class rules. */
function collectSlotted(c: Collector): void {
  const { state, level } = c;
  const tables = state.catalogue?.spellcasting?.slots ?? {};
  // Under Dual Class (#12) both classes are Casting Sources; the primary comes first in the item order.
  for (const classItem of state.items.filter((i) => i.role === "class")) {
    const table = tables[classItem.slug];
    const rules = CLASS_CASTING[classItem.slug];
    if (table && !rules) c.unrecognised.push({ uuid: classItem.uuid, name: classItem.name, reason: "a caster class the app's casting table does not describe" });
    if (table && rules) {
      const tradition = rules.tradition === "feature" ? featureTradition(state, classItem.slug) : rules.tradition;
      const problems: string[] = [];
      if (!tradition) problems.push("The tradition is unknown until the feature that names it (a bloodline, patron or eidolon) is chosen.");
      c.sources.push(
        c.byId.set(`class:${classItem.slug}`, {
          id: `class:${classItem.slug}`,
          kind: "class",
          name: `${classItem.name} Spells`,
          item: classItem,
          classSlug: classItem.slug,
          tradition,
          casting: rules.casting,
          attribute: rules.attribute === "key" ? keyAttribute(state) : rules.attribute,
          rank: "base",
          slots: classSlots(table, level),
          preparesFrom: rules.casting === "prepared" ? rules.preparesFrom : null,
          restrictedTo: null,
          statisticOf: null,
          autoPrepared: null,
          charges: null,
          granted: [],
          choices: [],
          problems,
        }).get(`class:${classItem.slug}`)!,
      );
      // A class whose table folds a second caster into its cells (the animist's apparition spells): a spontaneous Entry of the same tradition.
      if (table.secondary && rules.secondary) {
        const id = `class:${classItem.slug}:${rules.secondary.id}`;
        c.sources.push(
          c.byId.set(id, {
            id,
            kind: "class",
            name: rules.secondary.name,
            item: classItem,
            classSlug: classItem.slug,
            tradition,
            casting: rules.secondary.casting,
            attribute: rules.attribute === "key" ? keyAttribute(state) : rules.attribute,
            rank: "base",
            slots: classSlots(table.secondary, level),
            preparesFrom: null,
            restrictedTo: null,
            statisticOf: `class:${classItem.slug}`,
            autoPrepared: null,
            charges: null,
            granted: [],
            choices: [],
            problems: [],
          }).get(id)!,
        );
      }
    }
  }
  for (const item of state.items) {
    if (item.type !== "feat" || !item.otherTags.includes(SPELLCASTING_DEDICATION_TAG)) continue;
    const classSlug = item.slug.replace(/-dedication$/, "");
    const rules = CLASS_CASTING[classSlug];
    if (!rules || classSlug === item.slug) {
      c.unrecognised.push({ uuid: item.uuid, name: item.name, reason: "a spellcasting dedication the app's casting table does not describe" });
      continue;
    }
    const tradition = rules.tradition === "feature" ? featureTradition(state, classSlug) : rules.tradition;
    const slots = Array<number>(11).fill(0);
    slots[0] = rules.dedicationCantrips;
    for (const tier of ["basic", "expert", "master"] as const) {
      if (!state.items.some((i) => i.type === "feat" && i.slug === `${tier}-${classSlug}-spellcasting`)) continue;
      for (const { rank, level: at } of ARCHETYPE_SLOT_LEVELS[tier]) if (level >= at) slots[rank] = 1;
    }
    const problems: string[] = [];
    if (!tradition) problems.push("The tradition is unknown until the feature that names it (a bloodline, patron or eidolon) is chosen.");
    const id = `archetype:${classSlug}`;
    c.sources.push(
      c.byId.set(id, {
        id,
        kind: "archetype",
        name: `${title(classSlug)} Archetype Spells`,
        item,
        classSlug,
        tradition,
        casting: rules.casting,
        attribute: spellcastingAttribute(description(item)) ?? (rules.attribute === "key" ? keyAttribute(state) : rules.attribute),
        rank: "base",
        slots,
        preparesFrom: rules.casting === "prepared" ? rules.preparesFrom : null,
        restrictedTo: null,
        statisticOf: null,
        autoPrepared: null,
        charges: null,
        granted: [],
        choices: [],
        problems,
      }).get(id)!,
    );
  }
  collectDescribedArchetypes(c);
}

/**
 * Archetype spellcasting a feat describes in prose rather than by Foundry's dedication tag (Red
 * Mantis Magic, #21): the basic, expert and master feats share a slug after the tier word, the
 * text names the tradition, whether spells are prepared or a repertoire, the cantrips and the
 * spellcasting attribute, and the slots follow the archetype progression. A school the feat
 * gives access to adds its curriculum to the spells the Entry may prepare.
 */
function collectDescribedArchetypes(c: Collector): void {
  const { state, level } = c;
  const described = new Map<string, { item: EngineItem; benefits: SpellcastingBenefits }[]>();
  for (const item of state.items) {
    if (item.type !== "feat" || item.role === "temporary" || item.otherTags.includes(SPELLCASTING_DEDICATION_TAG)) continue;
    const m = /^(basic|expert|master)-(.+)$/.exec(item.slug);
    if (!m || CLASS_CASTING[m[2]!.replace(/-spellcasting$/, "")]) continue;
    const benefits = spellcastingBenefits(description(item));
    if (!benefits) continue;
    const list = described.get(m[2]!) ?? [];
    list.push({ item, benefits });
    described.set(m[2]!, list);
  }
  for (const [key, feats] of described) {
    const lead = feats.find((f) => f.benefits.tier === "basic") ?? feats[0]!;
    const tradition = feats.map((f) => f.benefits.tradition).find((t) => t !== null) ?? null;
    const casting = feats.map((f) => f.benefits.casting).find((t) => t !== null) ?? null;
    if (!tradition || !casting) {
      c.unrecognised.push({ uuid: lead.item.uuid, name: lead.item.name, reason: `an archetype's spellcasting whose ${!tradition ? "tradition" : "casting type"} the text does not state` });
      continue;
    }
    const slots = Array<number>(11).fill(0);
    slots[0] = feats.map((f) => f.benefits.cantrips).find((n) => n !== null) ?? 2;
    for (const f of feats) for (const { rank, level: at } of ARCHETYPE_SLOT_LEVELS[f.benefits.tier]) if (level >= at) slots[rank] = 1;
    const id = `archetype:${key}`;
    const target = source(c, id, () => ({
      id,
      kind: "archetype",
      name: `${title(key)} Spells`,
      item: lead.item,
      classSlug: null,
      tradition,
      casting,
      attribute: spellcastingAttribute(description(lead.item)) ?? "cha",
      rank: "base",
      slots,
      preparesFrom: casting === "prepared" ? "tradition" : null,
      restrictedTo: null,
      statisticOf: null,
      autoPrepared: null,
      charges: null,
      granted: [],
      choices: [],
      problems: [],
    }));
    // "You also have access to the Red Mantis Magic School": the school's curriculum joins the Entry's list. The school Entry is loaded on request.
    for (const f of feats) {
      for (const uuid of f.benefits.schools) {
        const school = state.entries.get(uuid);
        if (!school) {
          state.needed.add(uuid);
          continue;
        }
        const html = (school.system.description as { value?: unknown } | undefined)?.value;
        for (const list of labelledSpellLists(typeof html === "string" ? html : "")) {
          if (!/curriculum/i.test(list.label)) continue;
          for (const group of list.groups) for (const facts of factsOf(state, group.spells)) grantKnown(c, target, facts, roleRank(group.role), f.item);
        }
      }
    }
  }
}

/**
 * A spellbook a feat gives a spontaneous caster (Arcane Evolution, #21): a prepared Entry with
 * no slots that holds the repertoire and what Learn a Spell adds, sharing the class statistic.
 * One of its spells joins the repertoire for the day at daily preparations; that pick is the
 * user's on the class Entry.
 */
function collectSpellbooks(c: Collector): void {
  const { state } = c;
  for (const item of state.items) {
    if (item.type === "spell" || item.role === "temporary" || item.type === "weapon") continue;
    const statement = spellbookStatement(description(item));
    if (!statement) continue;
    const classSource = classSourceFor(c, item);
    if (!classSource || classSource.casting !== "spontaneous") continue;
    const id = `spellbook:${item.slug}`;
    const stored = c.character.spellcasting.entries[classSource.id];
    const repertoire = new Map<string, GrantedSpell>();
    for (const g of classSource.granted) repertoire.set(g.uuid, { uuid: g.uuid, name: g.name, rank: null, uses: null, granter: item });
    for (const k of stored?.known ?? []) if (!repertoire.has(k.uuid)) repertoire.set(k.uuid, { uuid: k.uuid, name: nameOf(state, k.uuid), rank: null, uses: null, granter: item });
    source(c, id, () => ({
      id,
      kind: "spellbook",
      name: `${item.name} Spellbook`,
      item,
      classSlug: classSource.classSlug,
      tradition: statement.tradition ?? classSource.tradition,
      casting: "prepared",
      attribute: classSource.attribute,
      rank: "base",
      slots: Array(11).fill(0),
      preparesFrom: "spellbook",
      restrictedTo: null,
      statisticOf: classSource.id,
      autoPrepared: null,
      charges: null,
      granted: [...repertoire.values()],
      choices: [],
      problems: [`The ${classSource.name} repertoire is in this spellbook; a spell added here may be that day's signature spell or join the repertoire for the day (${item.name}).`],
    }));
  }
}

/**
 * Staves in the inventory (#21, ADR-0013): each is an Entry of casting type "items" whose spells
 * cost charges equal to their rank. The charges are the highest spell rank the Character casts
 * from any slotted Entry, set at daily preparations; the statistic is that of the slotted Entry
 * whose tradition holds the most of the staff's spells (a staff's spells must be on the caster's
 * spell list). Wands stay in the inventory as consumables with a daily use.
 */
function collectItems(c: Collector): void {
  const { state } = c;
  const slotted = c.sources.filter((s) => (s.kind === "class" || s.kind === "archetype" || s.kind === "manual") && (s.casting === "prepared" || s.casting === "spontaneous") && highestRank(s.slots) > 0);
  for (const item of state.items) {
    if (!(item instanceof EnginePhysical) || item.type !== "weapon" || !item.traits.includes("staff")) continue;
    const list = itemSpellList(description(item));
    if (list.length === 0) continue;
    const spells = list.flatMap((group) => factsOf(state, group.spells).map((facts) => ({ facts, rank: roleRank(group.role) })));
    const covers = (s: CastingSource): number => spells.filter(({ facts }) => s.tradition !== null && facts.traditions.includes(s.tradition)).length;
    const statistic = [...slotted].sort((a, b) => covers(b) - covers(a))[0] ?? null;
    const charges = slotted.reduce((best, s) => Math.max(best, highestRank(s.slots)), 0);
    const problems: string[] = [];
    if (!statistic) problems.push("A staff needs a spellcasting class feature: no slotted Spellcasting Entry to prepare it with, so it has no charges.");
    else if (covers(statistic) < spells.length) problems.push(`Only the ${statistic.tradition ? TRADITION_LABELS[statistic.tradition].toLowerCase() : ""} spells on the staff can be cast from it.`);
    if (!item.isHeld) problems.push("Hold the staff to cast from it.");
    const id = `item:${item.inventoryId}`;
    source(c, id, () => ({
      id,
      kind: "item",
      name: item.name,
      item,
      classSlug: statistic?.classSlug ?? null,
      tradition: statistic?.tradition ?? null,
      casting: "items",
      attribute: statistic?.attribute ?? "cha",
      rank: "base",
      slots: Array(11).fill(0),
      preparesFrom: null,
      restrictedTo: null,
      statisticOf: statistic?.id ?? null,
      autoPrepared: null,
      charges,
      granted: spells.map(({ facts, rank }) => ({ uuid: facts.uuid, name: facts.name, rank: facts.cantrip ? null : rank, uses: null, granter: item })),
      choices: [],
      problems,
    }));
  }
}

/** The cleric's divine font: 4, 5 or 6 slots of the highest rank holding heal or harm (the Divine Font feature's answer). */
function collectFont(c: Collector): void {
  const cleric = c.byId.get("class:cleric");
  if (!cleric) return;
  const font = c.state.items.find((i) => i.slug === "divine-font" || i.slug === "healing-font" || i.slug === "harmful-font");
  if (!font) return;
  const top = highestRank(cleric.slots);
  if (top === 0) return;
  const answer = font.answers.divineFont;
  const fonts = c.state.actor.system.details.deities.primary?.font ?? [];
  const kind = font.slug === "healing-font" ? "heal" : font.slug === "harmful-font" ? "harm" : answer === "heal" || answer === "harm" ? answer : fonts.length === 1 ? fonts[0]! : null;
  const problems: string[] = [];
  if (!kind) problems.push("Choose heal or harm on the Divine Font feature.");
  const slots = Array<number>(11).fill(0);
  const count = font.slug === "divine-font" ? divineFontSlots(c.level) : Math.max(1, 1 + c.state.actor.system.abilities.cha.mod);
  slots[top] = count;
  const spell = kind === "heal" ? HEAL_UUID : kind === "harm" ? HARM_UUID : null;
  c.sources.push(
    c.byId.set("font:cleric", {
      id: "font:cleric",
      kind: "font",
      name: kind === "heal" ? "Divine Font (Healing)" : kind === "harm" ? "Divine Font (Harmful)" : "Divine Font",
      item: font,
      classSlug: "cleric",
      tradition: cleric.tradition,
      casting: "prepared",
      attribute: cleric.attribute,
      rank: "base",
      slots,
      preparesFrom: "tradition",
      restrictedTo: spell ? [spell] : [],
      statisticOf: cleric.id,
      autoPrepared: spell,
      charges: null,
      granted: [],
      choices: [],
      problems,
    }).get("font:cleric")!,
  );
}

/** Spells the held features grant to the slotted sources: bloodline granted spells, a school's curriculum, a familiar's spells, the deity's spells. */
function collectGrantedKnown(c: Collector): void {
  const { state } = c;
  for (const item of state.items) {
    if (item.type === "spell" || item.role === "temporary") continue;
    const html = description(item);
    if (!html.includes("spells-srd")) continue;
    const target = classSourceFor(c, item);
    const lists = labelledSpellLists(html);
    for (const list of lists) {
      // A list by rank ("Granted Spells", "Sorcerous Gifts", a curriculum) adds to the spells known; focus cantrip lists are the focus Entry's.
      const ranked = list.groups.some((g) => g.role === "cantrip" || typeof g.role === "number");
      if (!ranked || FOCUS_LIST_LABEL.test(list.label)) continue;
      if (!target) continue;
      // A list named for a class's second caster ("Apparition Spells") fills that Entry.
      const rules = target.classSlug ? CLASS_CASTING[target.classSlug] : undefined;
      const secondary = rules?.secondary && new RegExp(rules.secondary.listLabel, "i").test(list.label) ? c.byId.get(`${target.id}:${rules.secondary.id}`) : undefined;
      const into = secondary ?? target;
      for (const group of list.groups) for (const facts of factsOf(state, group.spells)) grantKnown(c, into, facts, roleRank(group.role), item);
      if (/curriculum/i.test(list.label) && target.kind === "class" && target.classSlug === "wizard") {
        const restricted = list.groups.flatMap((g) => g.spells.map((s) => s.uuid));
        const slots = Array<number>(11).fill(0);
        slots[0] = 1;
        for (let rank = 1; rank <= 10; rank++) if ((target.slots[rank] ?? 0) > 0) slots[rank] = 1;
        const id = `curriculum:${item.slug}`;
        source(c, id, () => ({
          id,
          kind: "curriculum",
          name: `${item.name} Curriculum`,
          item,
          classSlug: "wizard",
          tradition: target.tradition,
          casting: "prepared",
          attribute: target.attribute,
          rank: "base",
          slots,
          preparesFrom: "spellbook",
          restrictedTo: restricted,
          statisticOf: target.id,
          autoPrepared: null,
          charges: null,
          granted: restricted.map((uuid) => ({ uuid, name: nameOf(state, uuid), rank: null, uses: null, granter: item })),
          choices: [],
          problems: [],
        }));
      }
    }
    if (target) {
      for (const sentence of grantSentences(html)) {
        if (!sentence.familiar && !sentence.repertoire) continue;
        // "You add X to your apparition spell repertoire" names the class's second caster; a familiar's spells join the class Entry.
        const rules = target.classSlug ? CLASS_CASTING[target.classSlug] : undefined;
        const into = (sentence.repertoire && rules?.secondary && sentence.repertoire === rules.secondary.id ? c.byId.get(`${target.id}:${rules.secondary.id}`) : undefined) ?? target;
        for (const facts of factsOf(state, sentence.spells)) grantKnown(c, into, facts, null, item);
      }
    }
  }
  const deity = state.actor.system.details.deities.primary;
  const cleric = c.sources.find((s) => (s.kind === "class" || s.kind === "archetype") && s.classSlug === "cleric");
  const deityItem = state.items.find((i) => i.type === "deity");
  if (deity && cleric && deityItem) {
    for (const [rank, uuid] of Object.entries(deity.spells)) {
      const facts = spellFacts(state, uuid);
      if (facts) grantKnown(c, cleric, facts, Number(rank) || null, deityItem);
    }
  }
}

/** Innate spells the held Entries' descriptions grant or offer. */
function collectInnate(c: Collector): void {
  const { state } = c;
  for (const item of state.items) {
    if (item.type === "spell" || item.role === "temporary") continue;
    const html = description(item);
    // "Your alternate form is a fox, which has the statistics of 1st-rank Pest Form" (#21): an at-will innate spell at that rank.
    for (const form of formStatements(html)) {
      for (const facts of factsOf(state, form.spells)) {
        const tradition = facts.traditions[0] ?? null;
        if (!tradition || facts.focus || facts.ritual) continue;
        const target = innateSource(c, tradition, item);
        if (target.granted.some((g) => g.uuid === facts.uuid && g.granter === item)) continue;
        target.granted.push({ uuid: facts.uuid, name: facts.name, rank: facts.cantrip ? null : form.rank, uses: null, granter: item });
      }
    }
    if (!/\binnate\b/i.test(html)) continue;
    for (const statement of innateStatements(html)) {
      if (statement.choose) {
        const candidates = statement.choose.candidates ? factsOf(state, statement.choose.candidates).filter((f) => !f.focus && !f.ritual) : null;
        const tradition = statement.choose.tradition ?? statement.tradition ?? candidates?.[0]?.traditions[0] ?? null;
        if (!tradition) continue;
        const target = innateSource(c, tradition, item);
        if (candidates) {
          // "Choose either Bless or Bane. You can cast the chosen spell once per day as a 1st-rank divine innate spell."
          if (candidates.length === 0) continue;
          addChoice(target, item, `${item.name}: choose ${candidates.map((f) => f.name).join(" or ")}`, candidates.map((f) => f.uuid), { cantrip: false, rank: statement.choose.rank, tradition: null, focus: false, trait: null }, statement.uses);
          continue;
        }
        addChoice(target, item, `${item.name}: choose ${statement.choose.cantrip ? "a cantrip" : statement.choose.rank ? `a spell of rank ${statement.choose.rank} or lower` : "a spell"} from the ${TRADITION_LABELS[tradition].toLowerCase()} list`, null, {
          cantrip: statement.choose.cantrip,
          rank: statement.choose.cantrip ? 0 : statement.choose.rank,
          tradition,
          focus: false,
          trait: null,
        }, statement.choose.cantrip ? null : statement.uses);
        continue;
      }
      for (const facts of factsOf(state, statement.spells)) {
        if (facts.focus || facts.ritual) continue;
        const tradition = statement.tradition ?? facts.traditions[0] ?? null;
        if (!tradition) continue;
        const target = innateSource(c, tradition, item);
        if (target.granted.some((g) => g.uuid === facts.uuid && g.granter === item)) continue;
        target.granted.push({ uuid: facts.uuid, name: facts.name, rank: facts.cantrip ? null : statement.rank, uses: facts.cantrip ? null : statement.uses, granter: item });
      }
    }
  }
}

/** Labels of lists whose cantrips are focus cantrips whatever their traits say (psi cantrips are common cantrips cast as focus cantrips). */
const FOCUS_LIST_LABEL = /psi cantrip|hex cantrip|composition cantrip|focus cantrip|unique psi|standard psi/i;

/** Whether a held Entry unlocks the advanced or greater tier of another Entry's focus spells ("You gain the advanced bloodline spell"). */
function tierUnlocked(c: Collector, tier: "advanced" | "greater", listItem: EngineItem): boolean {
  const traits = new Set(classTraitsOf(listItem));
  return c.state.items.some((i) => i !== listItem && i.type === "feat" && (traits.size === 0 || i.traits.some((t) => traits.has(t))) && new RegExp(`\\bgain (?:the |your )?${tier} [a-z' ]*spell\\b`, "i").test(description(i).replace(/<[^>]+>/g, " ")));
}

/**
 * The animist's primary apparition (#21): the first attuned (the lowest Level and ordinal among
 * the `apparition` Choices). Its vessel spell is the one the animist has; the others' are not
 * known, so the pool starts at 1 as the class feature says and Third and Fourth Apparition add
 * theirs. Which apparition is primary changes at Refocus in play; here it is the Choice order.
 */
export function primaryApparition(state: EngineState): EngineItem | null {
  const rank = (item: EngineItem): number => {
    const m = /^(\d+):progression:apparition:(\d+)$/.exec(item.choiceKey ?? "");
    return m ? Number(m[1]) * 100 + Number(m[2]) : Number.MAX_SAFE_INTEGER;
  };
  const apparitions = state.items.filter((i) => i.otherTags.includes(APPARITION_TAG) && i.role !== "temporary");
  return apparitions.sort((a, b) => rank(a) - rank(b))[0] ?? null;
}

/** Focus spells the held Entries grant or offer: labelled lists by tier, grant sentences, domains. */
function collectFocus(c: Collector): void {
  const { state } = c;
  const domains = state.catalogue?.spellcasting?.domains ?? {};
  const initialDomains: string[] = [];
  const primary = primaryApparition(state);
  for (const item of state.items) {
    if (item.type === "spell" || item.role === "temporary") continue;
    // Only the primary apparition's vessel spell is the animist's.
    if (item.otherTags.includes(APPARITION_TAG) && item !== primary) continue;
    // Domain Initiate (and feats answering a domain the same way): the domain's initial spell.
    for (const [flag, value] of Object.entries(item.answers)) {
      if (!/domain/i.test(flag) || typeof value !== "string") continue;
      const domain = domains[value] ?? domains[value.replace(/-apocryphal$/, "")];
      if (!domain) continue;
      if (/advanced/i.test(flag)) continue;
      initialDomains.push(value.replace(/-apocryphal$/, ""));
      const facts = spellFacts(state, domain.initial);
      if (facts) grantFocus(c, item, facts);
    }
    const html = description(item);
    if (!html.includes("spells-srd")) continue;
    const seen = new Set<string>();
    for (const list of labelledSpellLists(html)) {
      const focusList = FOCUS_LIST_LABEL.test(list.label);
      for (const group of list.groups) {
        const facts = factsOf(state, group.spells).filter((f) => f.focus || (focusList && f.cantrip));
        if (facts.length === 0) continue;
        if (group.role === "advanced" || group.role === "greater") {
          if (!tierUnlocked(c, group.role, item)) continue;
        }
        for (const f of facts) {
          seen.add(f.uuid);
          grantFocus(c, item, f);
        }
      }
    }
    for (const sentence of grantSentences(html)) {
      if (sentence.familiar) continue;
      const facts = factsOf(state, sentence.spells).filter((f) => f.focus && !seen.has(f.uuid));
      if (facts.length === 0) continue;
      if (sentence.choose) {
        const target = focusSource(c, item, facts[0]!);
        const trait = sentence.another?.trait && (sentence.another.trait in CLASS_CASTING || sentence.another.trait in FOCUS_OWNERS) ? sentence.another.trait : null;
        addChoice(target, item, `${item.name}: choose a focus spell`, sentence.another ? null : facts.map((f) => f.uuid), { cantrip: false, rank: sentence.another?.rank ?? null, tradition: null, focus: true, trait: trait ?? (sentence.another ? (classTraitsOf(item)[0] ?? null) : null) });
      } else {
        for (const f of facts) grantFocus(c, item, f);
      }
    }
  }
  // "You gain a devotion spell of your choice listed in the devotion spells class feature" (#21): the pick is that feature's; its Entry is loaded on request.
  for (const item of state.items) {
    if (item.type === "spell" || item.role === "temporary") continue;
    for (const reference of featureReferences(description(item))) {
      const record = state.catalogue?.records.find((r) => r.p === "classfeatures" && r.t === "feat" && r.n.toLowerCase() === reference.feature);
      if (!record) continue;
      const feature = state.entries.get(record.u);
      if (!feature) {
        state.needed.add(record.u);
        continue;
      }
      const html = (feature.system.description as { value?: unknown } | undefined)?.value;
      const sentence = grantSentences(typeof html === "string" ? html : "").find((g) => g.choose && !g.familiar);
      const facts = sentence ? factsOf(state, sentence.spells).filter((f) => f.focus) : [];
      if (facts.length === 0) continue;
      const target = focusSource(c, item, facts[0]!);
      addChoice(target, item, `${item.name}: choose a ${reference.what}`, facts.map((f) => f.uuid), { cantrip: false, rank: null, tradition: null, focus: true, trait: null });
    }
  }
  // Advanced Domain: one advanced domain spell per feat, from the domains with an initial spell.
  for (const item of state.items) {
    if (item.type !== "feat" || item.slug !== "advanced-domain") continue;
    const candidates = initialDomains.map((d) => domains[d]?.advanced).filter((u): u is string => typeof u === "string");
    const target = focusSource(c, item, candidates[0] ? spellFacts(state, candidates[0]) : null);
    addChoice(target, item, `${item.name}: choose an advanced domain spell`, candidates, { cantrip: false, rank: null, tradition: null, focus: true, trait: null });
  }
}

function collectManual(c: Collector): void {
  for (const manual of c.character.spellcasting.manual) {
    const slots = Array<number>(11).fill(0);
    for (const [rank, count] of Object.entries(manual.slots)) if (Number(rank) >= 0 && Number(rank) <= 10) slots[Number(rank)] = count;
    c.sources.push(
      c.byId.set(manual.id, {
        id: manual.id,
        kind: "manual",
        name: manual.name,
        item: null,
        classSlug: null,
        tradition: manual.tradition,
        casting: manual.casting,
        attribute: isAttribute(manual.attribute) ? manual.attribute : "cha",
        rank: "base",
        slots,
        preparesFrom: manual.casting === "prepared" ? "spellbook" : null,
        restrictedTo: null,
        statisticOf: null,
        autoPrepared: null,
        charges: null,
        granted: [],
        choices: [],
        problems: [],
      }).get(manual.id)!,
    );
  }
}

/** Every spell UUID a source and the user's state on it name. */
function spellUuids(source: CastingSource, state: SpellcastingState | undefined): string[] {
  const out = new Set<string>();
  for (const g of source.granted) out.add(g.uuid);
  if (source.autoPrepared) out.add(source.autoPrepared);
  for (const k of state?.known ?? []) out.add(k.uuid);
  for (const list of Object.values(state?.prepared ?? {})) for (const u of list) if (u) out.add(u);
  return [...out];
}

/**
 * Phase A. Collect the Casting Sources, then what Foundry sets before Rule Elements run: the
 * spellcasting proficiency floor (trained with any Entry; innate spells make it trained, expert
 * from Level 12), the focus pool from focus spells known, and the `self:caster` roll options.
 * Spell Entries the sources name that are not loaded are added to `needed`.
 */
export function collectCastingSources(state: EngineState, character: Character): SpellcastingSourcesResult {
  choiceCounter = 0;
  const c: Collector = { state, character, level: state.level, sources: [], byId: new Map(), unrecognised: [] };
  collectSlotted(c);
  collectFont(c);
  collectGrantedKnown(c);
  collectSpellbooks(c);
  collectInnate(c);
  collectFocus(c);
  collectManual(c);
  collectItems(c);
  const order: Record<SourceKind, number> = { class: 0, font: 1, curriculum: 2, spellbook: 3, archetype: 4, focus: 5, innate: 6, manual: 7, item: 8 };
  c.sources.sort((a, b) => order[a.kind] - order[b.kind]);

  const proficiency = state.actor.system.proficiencies.spellcasting;
  // A staff alone makes no caster: it is cast through another Entry's statistic.
  const casts = c.sources.some((s) => s.kind !== "item");
  if (c.sources.some((s) => s.kind === "innate")) proficiency.rank = Math.max(proficiency.rank, state.level >= 12 ? 2 : 1) as Rank;
  else if (casts) proficiency.rank = Math.max(proficiency.rank, 1) as Rank;

  // The focus pool: one point per non-cantrip focus spell known, before Rule Elements add theirs; the cap applies at the end.
  const focusSpells = new Set<string>();
  for (const s of c.sources) {
    if (s.casting !== "focus") continue;
    const stored = character.spellcasting.entries[s.id];
    for (const uuid of spellUuids(s, stored)) {
      const facts = spellFacts(state, uuid);
      if (facts && facts.focus && !facts.cantrip) focusSpells.add(uuid);
    }
  }
  state.actor.system.resources.focus.max += focusSpells.size;

  for (const s of c.sources) {
    const stored = character.spellcasting.entries[s.id];
    const uuids = spellUuids(s, stored);
    if (uuids.length > 0 && s.kind !== "item") {
      state.rollOptions.set("all", "self:caster");
      if (s.tradition) state.rollOptions.set("all", `self:caster:tradition:${s.tradition}`);
    }
    for (const uuid of uuids) if (!state.entries.has(uuid) && !spellFacts(state, uuid)?.entry) state.needed.add(uuid);
  }
  return { sources: c.sources, unrecognised: c.unrecognised };
}

/**
 * The animist's apparition attunement (#21): Apparition Attunement opens two `apparition`
 * progression Choices at its Level, Third Apparition and Fourth Apparition one more each at
 * theirs (the guide opens them, src/guide/slots.ts). Index is the Level; the count is what
 * opens at that Level.
 */
export function apparitionSlotTrack(state: EngineState): { opened: number[]; total: number } {
  const opened: number[] = [0];
  let total = 0;
  const openAt = (level: number, count: number): void => {
    const at = Math.min(state.level, Math.max(1, level));
    opened[at] = (opened[at] ?? 0) + count;
    total += count;
  };
  for (const item of state.items) {
    if (item.type !== "feat" || item.role === "temporary") continue;
    if (item.slug === "apparition-attunement") openAt(item.level ?? 1, 2);
    else if (item.slug === "third-apparition" || item.slug === "fourth-apparition") openAt(item.level ?? 1, 1);
  }
  for (let level = 0; level <= state.level; level++) opened[level] ??= 0;
  return { opened, total };
}

/* ------------------------------------------------------------------------ */
/*  Phase B: statistics                                                      */
/* ------------------------------------------------------------------------ */

/** Foundry's `SpellcastingEntryPF2e#getRollOptions("spellcasting")`. */
export function sourceRollOptions(source: CastingSource): string[] {
  const tradition = source.tradition ?? "arcane";
  return [
    `spellcasting:${source.attribute}`,
    `spellcasting:attribute:${source.attribute}`,
    `spellcasting:${source.casting}`,
    `spellcasting:category:${source.casting}`,
    `spellcasting:${tradition}`,
    `spellcasting:tradition:${tradition}`,
  ];
}

/** Each Entry's proficiency: the Character's spellcasting rank, or its own where higher. */
export function sourceRank(state: EngineState, source: CastingSource): Rank {
  const base = state.actor.system.proficiencies.spellcasting.rank;
  return source.rank === "base" ? base : (Math.max(source.rank, base) as Rank);
}

/**
 * Phase B. One statistic per Casting Source with its own (Foundry: `prepareStatistic`): the base
 * spellcasting domains, the attribute, the tradition's attack and DC domains. Sets the best spell
 * DC on the attributes as Foundry does.
 */
export function spellcastingStatistics(state: EngineState, sources: CastingSource[]): Record<string, DerivedStatistic> {
  const out: Record<string, DerivedStatistic> = {};
  const attributes = state.actor.system.attributes;
  for (const source of sources) {
    if (source.statisticOf) continue;
    const tradition = source.tradition ?? "arcane";
    const rank = sourceRank(state, source);
    const statistic = buildStatistic(state, {
      slug: sluggify(source.id.replace(/:/g, "-")),
      label: source.tradition ? TRADITION_LABELS[source.tradition] : source.name,
      attribute: source.attribute,
      rank,
      domains: ["all", `${source.attribute}-based`, "spell-attack-dc"],
      rollOptions: sourceRollOptions(source),
      check: { type: "attack-roll", domains: [`${tradition}-spell-attack`, "spell-attack", "spell-attack-roll", "attack", "attack-roll"] },
      dc: { domains: [`${tradition}-spell-dc`, "spell-dc"] },
    });
    out[source.id] = statistic;
    const dc = { rank, value: statistic.dc.value };
    if (dc.value > attributes.classOrSpellDC.value) attributes.classOrSpellDC = dc;
    if (!attributes.spellDC || dc.value > attributes.spellDC.value) attributes.spellDC = dc;
  }
  return out;
}

/** Foundry's `actor.getStatistic("<tradition>")`: the best spellcasting statistic of a tradition. */
export function traditionStatistic(state: EngineState, tradition: string): DerivedStatistic | null {
  const derived = state.derived;
  if (!derived) return null;
  const candidates = state.spellcasting.sources.filter((s) => s.tradition === tradition && !s.statisticOf).map((s) => derived.spellcastingEntries[s.id]).filter((st): st is DerivedStatistic => !!st);
  return candidates.sort((a, b) => b.check.mod - a.check.mod)[0] ?? null;
}

/* ------------------------------------------------------------------------ */
/*  Phase C: views                                                           */
/* ------------------------------------------------------------------------ */

export interface SpellView {
  uuid: string;
  name: string;
  /** Whether the Entry was loaded; an unloaded spell shows its index name and no heightened text. */
  loaded: boolean;
  baseRank: number;
  /** The rank it is cast at here: a slot's rank, the known rank, or half the Level rounded up. */
  rank: number;
  cantrip: boolean;
  focus: boolean;
  traditions: string[];
  rarity: string | null;
  remaster: boolean | null;
  signature: boolean;
  /** The Entry that grants it, or null for the user's own pick. */
  grantedBy: { uuid: string; name: string } | null;
  /** The spell Choice it answers, if any. */
  choice: string | null;
  /** Innate: uses per day; null for at will. */
  uses: number | null;
  heightened: { damage: HeightenedDamage[]; changes: string[]; overlays: string[] } | null;
  traits: string[];
  /** Whether the spell makes a spell attack roll (the attack trait). */
  attack: boolean;
  /** The save the spell calls for, when it does. */
  save: { statistic: string; basic: boolean } | null;
  /** The damage at the cast rank with every synthetic applied (#19 rolls it); null when the spell deals none. */
  damage: SpellDamageView | null;
  problems: string[];
}

export interface SpellChoiceView {
  key: string;
  prompt: string;
  granter: { uuid: string; name: string };
  candidates: string[] | null;
  filter: SpellChoiceFilter;
  /** The chosen spell's UUID, when answered. */
  answered: string | null;
}

export interface SpellcastingEntryView<S> {
  id: string;
  name: string;
  kind: SourceKind;
  casting: CastingType;
  castingLabel: string;
  tradition: MagicTradition | null;
  traditionLabel: string;
  attribute: Attribute;
  rank: Rank;
  source: { uuid: string; name: string } | null;
  classSlug: string | null;
  /** The attack (check) and DC. Null only when the Entry shares another's and that one is missing. */
  statistic: S | null;
  /** The id of the Entry whose statistic this one shares, if any. */
  statisticOf: string | null;
  /** Slots per rank ("0" cantrips to "10"); ranks without slots are absent. */
  slots: Record<string, number>;
  /** The highest rank with a slot; 0 when only cantrips. */
  highestRank: number;
  /** A staff's charges (#21), set at daily preparations; null for every other Entry. */
  charges: number | null;
  /** Spells known: repertoire, spellbook, innate spells, focus spells, a staff's list. */
  spells: SpellView[];
  /** Prepared Entries: by rank, the spell in each slot. */
  prepared: Record<string, (SpellView | null)[]>;
  /** Spontaneous Entries: repertoire limits per rank, counting the user's picks only. */
  limits: Record<string, { max: number; used: number }> | null;
  preparesFrom: "spellbook" | "tradition" | null;
  restrictedTo: string[] | null;
  choices: SpellChoiceView[];
  problems: string[];
}

function spellSave(entry: Entry | null): SpellView["save"] {
  const defense = entry?.system.defense;
  if (typeof defense !== "object" || defense === null) return null;
  const save = (defense as { save?: unknown }).save;
  if (typeof save !== "object" || save === null) return null;
  const statistic = (save as { statistic?: unknown }).statistic;
  return typeof statistic === "string" && statistic ? { statistic, basic: (save as { basic?: unknown }).basic === true } : null;
}

function spellView(state: EngineState, uuid: string, rank: number | null, extra: Partial<SpellView>, source: CastingSource | null = null): SpellView {
  const facts = spellFacts(state, uuid);
  const level = state.level;
  const problems: string[] = [];
  if (!facts) problems.push("This spell is not in the Snapshot.");
  else if (facts.ritual) problems.push("Rituals are out of scope.");
  const entry = facts?.entry ?? null;
  const cast = entry ? castRank(entry, level, rank) : facts ? (facts.cantrip || facts.focus ? Math.max(facts.baseRank, Math.ceil(level / 2)) : Math.max(facts.baseRank, rank ?? facts.baseRank)) : (rank ?? 1);
  const heightened = entry ? heightenSpell(entry, cast) : null;
  const damage = entry && heightened && source ? calculateSpellDamage(state, entry, heightened, source, facts?.cantrip ?? false) : null;
  return {
    uuid,
    name: facts?.name ?? uuid.slice(uuid.lastIndexOf(".") + 1),
    loaded: entry !== null,
    baseRank: facts?.baseRank ?? 1,
    rank: Math.min(10, cast),
    cantrip: facts?.cantrip ?? false,
    focus: facts?.focus ?? false,
    traditions: facts?.traditions ?? [],
    rarity: facts?.rarity ?? null,
    remaster: facts?.remaster ?? null,
    signature: false,
    grantedBy: null,
    choice: null,
    uses: null,
    heightened: heightened ? { damage: heightened.damage, changes: heightened.changes, overlays: heightened.overlays } : null,
    traits: facts?.traits ?? [],
    attack: facts?.traits.includes("attack") ?? false,
    save: spellSave(entry),
    damage: damage ? { formula: { ...damage.formula }, breakdown: { failure: [...damage.breakdown.failure], success: [...damage.breakdown.success], criticalSuccess: [...damage.breakdown.criticalSuccess] }, modifiers: damage.modifiers.map(modifierView), dice: damage.dice.map(damageDiceView), kinds: damage.kinds, simple: damage.simple } : null,
    problems,
    ...extra,
  };
}

/** Whether a spell is on the Entry's list: its tradition, or known to the Entry (a granted or learned spell). */
function onList(source: CastingSource, facts: SpellFacts | null, known: SpellView[]): boolean {
  if (!facts) return true;
  if (known.some((k) => k.uuid === facts.uuid)) return true;
  return source.tradition !== null && facts.traditions.includes(source.tradition);
}

/**
 * Phase C. The sheet's view of every Spellcasting Entry: statistic, slots, spells at their cast
 * ranks, preparations checked structurally (a spell no higher than its slot, cantrips in cantrip
 * slots, restricted slots), repertoire limits, and the spell Choices still open.
 */
export function spellcastingViews<S>(state: EngineState, character: Character, sources: CastingSource[], statistics: Record<string, DerivedStatistic>, toView: (statistic: DerivedStatistic, source: CastingSource) => S): SpellcastingEntryView<S>[] {
  const views: SpellcastingEntryView<S>[] = [];
  for (const source of sources) {
    const stored = character.spellcasting.entries[source.id];
    const problems = [...source.problems];
    const spells: SpellView[] = [];
    for (const g of source.granted) {
      // The same innate spell from two Entries is two spells with their own uses; elsewhere a spell is known once per rank.
      if (source.casting !== "innate" && spells.some((s) => s.uuid === g.uuid && (source.casting !== "spontaneous" || s.rank === (g.rank ?? s.baseRank)))) continue;
      spells.push(spellView(state, g.uuid, g.rank, { grantedBy: { uuid: g.granter.uuid, name: g.granter.name }, uses: g.uses }, source));
    }
    const answers = new Map<string, string>();
    const redundant = new Set<KnownSpell>();
    for (const k of stored?.known ?? []) {
      const facts = spellFacts(state, k.uuid);
      const duplicate = source.casting === "innate" ? undefined : spells.find((s) => s.uuid === k.uuid && (source.casting !== "spontaneous" || s.rank === (k.rank ?? s.baseRank)));
      const choice = k.choice ? source.choices.find((ch) => ch.key === k.choice) : undefined;
      const uses = source.casting !== "innate" ? null : facts?.cantrip ? null : choice ? choice.uses : 1;
      const view = spellView(state, k.uuid, k.rank ?? null, { signature: k.signature === true, choice: k.choice ?? null, uses }, source);
      if (k.choice) answers.set(k.choice, k.uuid);
      if (duplicate) {
        if (k.signature) duplicate.signature = true;
        duplicate.problems.push("Also granted by an Entry; the pick is redundant.");
        redundant.add(k);
        continue;
      }
      if (facts) {
        if (source.casting === "focus" && !facts.focus) view.problems.push("Not a focus spell.");
        if (source.casting !== "focus" && facts.focus) view.problems.push("A focus spell belongs on a focus Entry.");
        if (source.casting !== "focus" && !facts.cantrip && source.casting !== "innate" && !onList(source, facts, spells)) view.problems.push(`Not on the ${source.tradition ? TRADITION_LABELS[source.tradition].toLowerCase() : "Entry's"} spell list.`);
        if (source.casting === "spontaneous" && !facts.cantrip && view.rank > source.slots.length - 1) view.problems.push("Known at a rank above any slot.");
      }
      spells.push(view);
    }
    const choices: SpellChoiceView[] = source.choices.map((ch) => ({ key: ch.key, prompt: ch.prompt, granter: { uuid: ch.granter.uuid, name: ch.granter.name }, candidates: ch.candidates, filter: ch.filter, answered: answers.get(ch.key) ?? null }));

    const slots: Record<string, number> = {};
    source.slots.forEach((n, rank) => {
      if (n > 0) slots[String(rank)] = n;
    });
    const top = highestRank(source.slots);

    const prepared: Record<string, (SpellView | null)[]> = {};
    if (source.casting === "prepared") {
      for (const [rankKey, count] of Object.entries(slots)) {
        const rank = Number(rankKey);
        const storedList = stored?.prepared[rankKey] ?? [];
        const list: (string | null)[] = source.autoPrepared ? Array<string | null>(count).fill(source.autoPrepared) : Array.from({ length: count }, (_, i) => storedList[i] ?? null);
        prepared[rankKey] = list.map((uuid) => {
          if (!uuid) return null;
          const facts = spellFacts(state, uuid);
          const view = spellView(state, uuid, rank, {}, source);
          if (facts) {
            if (rank === 0 && !facts.cantrip) view.problems.push("Only a cantrip can be prepared in a cantrip slot.");
            if (rank > 0 && facts.cantrip) view.problems.push("A cantrip cannot be prepared in a ranked slot.");
            if (rank > 0 && facts.baseRank > rank) view.problems.push(`Rank ${facts.baseRank} spell in a rank ${rank} slot.`);
            if (source.restrictedTo && !source.restrictedTo.includes(uuid)) view.problems.push("Not one of the spells this slot may hold.");
            if (source.preparesFrom === "spellbook" && !spells.some((s) => s.uuid === uuid)) view.problems.push("Not in the spellbook.");
            if (source.preparesFrom === "tradition" && !onList(source, facts, spells)) view.problems.push(`Not on the ${source.tradition ? TRADITION_LABELS[source.tradition].toLowerCase() : "Entry's"} spell list.`);
          }
          return view;
        });
      }
      // Preparations stored for ranks the Entry no longer has slots at are reported, not dropped.
      for (const rankKey of Object.keys(stored?.prepared ?? {})) {
        if (!(rankKey in slots) && (stored?.prepared[rankKey] ?? []).some((u) => u)) problems.push(`Rank ${rankKey} preparations have no slots at this Level.`);
      }
    }

    let limits: SpellcastingEntryView<S>["limits"] = null;
    if (source.casting === "spontaneous") {
      limits = {};
      for (const [rankKey, max] of Object.entries(slots)) {
        const rank = Number(rankKey);
        const used = (stored?.known ?? []).filter((k) => {
          const facts = spellFacts(state, k.uuid);
          if (!facts) return false;
          if (k.choice || redundant.has(k)) return false;
          return rank === 0 ? facts.cantrip : !facts.cantrip && (k.rank ?? facts.baseRank) === rank;
        }).length;
        limits[rankKey] = { max, used };
        if (used > max) problems.push(`${used} rank ${rank} spells known for ${max} slots.`);
      }
    }

    const statisticSource = source.statisticOf ? sources.find((s) => s.id === source.statisticOf) : source;
    const statistic = statisticSource ? statistics[statisticSource.id] : undefined;
    views.push({
      id: source.id,
      name: source.name,
      kind: source.kind,
      casting: source.casting,
      castingLabel: CASTING_LABELS[source.casting],
      tradition: source.tradition,
      traditionLabel: source.tradition ? TRADITION_LABELS[source.tradition] : "Unknown tradition",
      attribute: source.attribute,
      rank: sourceRank(state, source),
      source: source.item ? { uuid: source.item.uuid, name: source.item.name } : null,
      classSlug: source.classSlug,
      statistic: statistic && statisticSource ? toView(statistic, statisticSource) : null,
      statisticOf: source.statisticOf,
      slots,
      highestRank: top,
      charges: source.charges,
      spells,
      prepared,
      limits,
      preparesFrom: source.preparesFrom,
      restrictedTo: source.restrictedTo,
      choices,
      problems,
    });
  }
  return views;
}
